import type { UnitType } from '@racemap/utilities/types/units';
import type {
  ExpressionSpecification,
  Map as MapboxMap,
  SymbolLayerSpecification,
} from 'mapbox-gl';
import { type FC, useId, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import {
  buildDistanceMarkerFeatures,
  type IndexedLine,
  visibleZoomBands,
} from '../helpers/distanceMarkers';
import { useMapImage } from '../helpers/useMapImage';

/** Every layer one instance adds starts with this, so callers can reorder them as a group. */
export const distanceMarkerLayerPrefix = (id: string): string => `layer-distance-marker-${id}-`;

export interface Props {
  line: IndexedLine;
  unitType: UnitType;
  /** Uniform index spacing in meters. Skips all distance measuring when known. */
  metersPerIndex?: number;
  /** Border of the bubble, to tie it to the track it measures. */
  strokeColor?: string;
  largeMode?: boolean;
  /** Hidden the mapbox way, not by unmounting: re-adding the layers re-uploads the GeoJSON. */
  visible?: boolean;
  id?: string;
  beforeId?: string;
}

export const DistanceMarkerLayer: FC<Props> = ({
  line,
  unitType,
  metersPerIndex,
  strokeColor = '#000000',
  largeMode = false,
  visible = true,
  id: settedId,
  beforeId,
}) => {
  const genId = useId();
  const id = settedId || genId;
  const featureCollection = useMemo(
    () => buildDistanceMarkerFeatures(line, unitType, metersPerIndex),
    [line, unitType, metersPerIndex],
  );
  const bubbleImageId = useMapImage(`distance-marker-bubble-${strokeColor}`, (map, imageId) =>
    addBubbleImage(map, imageId, strokeColor),
  );
  const layout = useMemo(
    () => (bubbleImageId == null ? null : getMarkerLayout(largeMode, bubbleImageId)),
    [largeMode, bubbleImageId],
  );
  const visibility = visible ? 'visible' : 'none';

  const bands = visibleZoomBands(featureCollection.features.length);
  const layerPrefix = distanceMarkerLayerPrefix(id);
  if (layout == null) return null;

  return (
    <Source
      type="geojson"
      data={featureCollection}
      id={`distance-markers-${id}`}
      key={`distance-markers-${id}`}
    >
      {/* An array, not fragments: <Source> injects its id into direct children only. */}
      {bands.map((band) => (
        <Layer
          id={`${layerPrefix}${band.stride}`}
          key={`${layerPrefix}${band.stride}`}
          type="symbol"
          filter={['==', ['%', ['get', 'distance'], band.stride], 0]}
          layout={{ ...layout, visibility }}
          paint={TEXT_PAINT}
          minzoom={band.minzoom}
          maxzoom={band.maxzoom}
          beforeId={beforeId}
        />
      ))}
    </Source>
  );
};

/** Sizes the bubbles by zoom. Below and above the outer stops the size holds. */
const zoomRamp = (
  stops: Array<[zoom: number, size: number]>,
  scale: number,
): ExpressionSpecification =>
  [
    'interpolate',
    ['linear'],
    ['zoom'],
    ...stops.flatMap(([zoom, size]) => [zoom, size * scale]),
  ] as ExpressionSpecification;

/** Small enough at overview zooms to leave the route visible, readable once zoomed in. */
const CIRCLE_RADIUS_STOPS: Array<[number, number]> = [
  [10, 7],
  [13, 9],
  [17, 16],
];
const TEXT_SIZE_STOPS: Array<[number, number]> = [
  [10, 7],
  [13, 9],
  [17, 14],
];

/** The bubble is drawn at its largest and scaled down by `icon-size`. */
const BUBBLE_RADIUS = 16;
/** About the former 2 px border at zoom 13, where the bubble's radius is 9. */
const BUBBLE_STROKE_WIDTH = 3.5;

/**
 * Bubble and number share one symbol layer: with a sort key, mapbox draws each marker's icon and
 * text together, so a later marker covers an earlier one whole. Separate layers drew every number
 * above every bubble.
 */
function getMarkerLayout(
  largeMode: boolean,
  bubbleImageId: string,
): SymbolLayerSpecification['layout'] {
  const scale = largeMode ? 1.5 : 1;

  return {
    'symbol-placement': 'point',
    'symbol-sort-key': ['get', 'distance'],
    'icon-image': bubbleImageId,
    'icon-size': zoomRamp(CIRCLE_RADIUS_STOPS, scale / BUBBLE_RADIUS),
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-field': '{distance}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
    'text-anchor': 'center',
    'text-justify': 'center',
    'text-size': zoomRamp(TEXT_SIZE_STOPS, scale),
    'text-allow-overlap': true,
    'text-ignore-placement': true,
  };
}

function addBubbleImage(map: MapboxMap, imageId: string, strokeColor: string): void {
  const pixelRatio = window.devicePixelRatio || 1;
  const half = (BUBBLE_RADIUS + BUBBLE_STROKE_WIDTH / 2) * pixelRatio;
  const size = Math.ceil(2 * half);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context == null) return;

  context.beginPath();
  context.arc(half, half, BUBBLE_RADIUS * pixelRatio, 0, 2 * Math.PI);
  context.fillStyle = '#ffffff';
  context.fill();
  context.lineWidth = BUBBLE_STROKE_WIDTH * pixelRatio;
  context.strokeStyle = strokeColor;
  context.stroke();

  map.addImage(imageId, context.getImageData(0, 0, size, size), { pixelRatio });
}

const TEXT_PAINT: SymbolLayerSpecification['paint'] = {
  'text-color': '#000000',
  'text-halo-color': '#ffffff',
  'text-halo-width': 1,
};
